import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { trackEvent } from "../lib/analytics";
import type { AssessmentAnswers, FrictionSnapshot, PublicSubmissionPayload } from "../types/friction-finder";
import { Button } from "./Button";

type ContactValues = { fullName: string; businessName: string; workEmail: string; consent: boolean; website: string };

type Props = {
  answers: AssessmentAnswers;
  snapshot: FrictionSnapshot;
  assessmentSessionId: string;
  onSuccess: () => void;
};

function ValidationMessage({ id, message }: { id?: string; message?: string }) {
  const reduceMotion = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {message && (
        <m.p
          className="validation-message"
          id={id}
          role="alert"
          initial={reduceMotion ? false : { opacity: 0.01, y: -3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -2 }}
          transition={{ duration: reduceMotion ? 0 : 0.14 }}
        >{message}</m.p>
      )}
    </AnimatePresence>
  );
}

export function ContactForm({ answers, snapshot, assessmentSessionId, onSuccess }: Props) {
  const [submissionError, setSubmissionError] = useState(false);
  const opened = useRef(false);
  const reduceMotion = useReducedMotion();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ContactValues>({
    defaultValues: { fullName: "", businessName: "", workEmail: "", consent: false, website: "" },
  });

  const markOpened = () => {
    if (!opened.current) {
      opened.current = true;
      trackEvent("friction_contact_opened", { assessment_session_id: assessmentSessionId, intent: snapshot.intent });
    }
  };

  const submit = handleSubmit(async (values) => {
    setSubmissionError(false);
    const payload: PublicSubmissionPayload = {
      fullName: values.fullName,
      businessName: values.businessName,
      workEmail: values.workEmail,
      consent: true,
      answers,
      customFriction: answers.customFriction,
      customOutcome: answers.customOutcome,
      clientSnapshot: snapshot,
      clientSnapshotText: snapshot.plainTextVersion,
      generatedAt: snapshot.generatedAt,
      assessmentSessionId,
      referrer: document.referrer || undefined,
      honeypot: values.website,
    };
    try {
      const response = await fetch("/api/submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Submission failed");
      trackEvent("friction_contact_submitted", { assessment_session_id: assessmentSessionId, intent: snapshot.intent });
      onSuccess();
    } catch {
      setSubmissionError(true);
      trackEvent("friction_contact_failed", { assessment_session_id: assessmentSessionId, intent: snapshot.intent });
    }
  });

  return (
    <m.section
      className="contact-section no-print"
      aria-labelledby="contact-heading"
      initial={reduceMotion ? false : { opacity: 0.78, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.2, delay: reduceMotion ? 0 : 0.12 }}
    >
      <h2 id="contact-heading">Talk through your Friction Snapshot</h2>
      <p>Request a free 20–30 minute conversation with HuddleCo to confirm the friction, why it matters and whether there is a practical next step.</p>
      <AnimatePresence initial={false}>
        {submissionError && (
          <m.div className="error-summary" role="alert" tabIndex={-1} initial={reduceMotion ? false : { opacity: 0.01, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0 }} transition={{ duration: reduceMotion ? 0 : 0.16 }}>
            <h3>Your snapshot could not be sent.</h3>
            <p>Your answers are still here. Check your connection and try again.</p>
          </m.div>
        )}
      </AnimatePresence>
      <form onSubmit={submit} noValidate onFocus={markOpened} aria-busy={isSubmitting}>
        <div className="field">
          <label htmlFor="full-name">Full name</label>
          <input id="full-name" autoComplete="name" aria-invalid={!!errors.fullName} aria-describedby={errors.fullName ? "full-name-error" : undefined} {...register("fullName", { required: true, minLength: 2, maxLength: 80 })} />
          <ValidationMessage id="full-name-error" message={errors.fullName ? "Enter your full name." : undefined} />
        </div>
        <div className="field">
          <label htmlFor="business-name">Business name</label>
          <input id="business-name" autoComplete="organization" aria-invalid={!!errors.businessName} aria-describedby={errors.businessName ? "business-name-error" : undefined} {...register("businessName", { required: true, minLength: 2, maxLength: 100 })} />
          <ValidationMessage id="business-name-error" message={errors.businessName ? "Enter your business name." : undefined} />
        </div>
        <div className="field">
          <label htmlFor="work-email">Work email</label>
          <input id="work-email" type="email" autoComplete="email" aria-invalid={!!errors.workEmail} aria-describedby={errors.workEmail ? "work-email-error" : undefined} {...register("workEmail", { required: true, maxLength: 160, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} />
          <ValidationMessage id="work-email-error" message={errors.workEmail ? "Enter a valid work email address." : undefined} />
        </div>
        <div className="honeypot" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input id="website" tabIndex={-1} autoComplete="off" {...register("website")} />
        </div>
        <div className="checkbox-field">
          <input id="consent" type="checkbox" aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "consent-error" : "privacy-note"} {...register("consent", { required: true })} />
          <label htmlFor="consent">I agree that HuddleCo may contact me to arrange this conversation.</label>
        </div>
        <ValidationMessage id="consent-error" message={errors.consent ? "Confirm that HuddleCo may contact you about this Snapshot." : undefined} />
        <p className="privacy-note" id="privacy-note">This step is optional. If you choose to request a conversation, please complete all fields. Read our <Link to="/privacy">privacy information</Link>.</p>
        <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "Sending…" : submissionError ? "Try again" : "Request a conversation"}</Button>
      </form>
    </m.section>
  );
}
